// Life-event radar on Today (owner-approved idea 34): what is coming in the clients' lives in the next 60 days, from the
// book itself — milestone birthdays, loans ending, policies maturing, children turning 18, baptism anniversaries, the
// yearly transparency day and risk profiles that no longer fit. One move per item. Logic: domain/src/life-radar.ts.
import { useMemo, useState } from 'react';
import { lifeEventRadar, radarTask, radarTaskId, type RadarItem } from '@vero/domain';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Avatar } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';
import { TransparencySummary } from './summaries.tsx';

const ICON: Record<RadarItem['kind'], string> = { milestone_birthday: 'cake', loan_ends: 'coins', policy_matures: 'policy', turns18: 'star', baptism: 'heart', transparency_day: 'document', risk_review: 'radar' };
export function radarText(rt: Runtime, it: RadarItem): string {
  const p: Record<string, string | number> = { ...it.params };
  if (it.kind === 'risk_review') { p.level = rt.t(`risk.level.${it.params.level}`); p.fits = it.params.fits ? rt.t(`risk.level.${it.params.fits}`) : ''; }
  return rt.t(`radar34.item.${it.kind}`, p);
}

export function LifeRadarCard() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const items = useMemo(() => lifeEventRadar(view, today, 60), [view, today]);
  const [all, setAll] = useState(false);
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, 6);
  return (
    <section className="card" data-testid="life-radar">
      <div className="card-head"><div><h2>{t('radar34.title')}</h2><div className="small muted">{t('radar34.subtitle', { count: items.length })}</div></div></div>
      <ul className="plain-list radar34-list">{shown.map((it) => {
        const p = view.get('persons', it.personId);
        const done = !!view.get('tasks', radarTaskId(it));
        return (
          <li key={it.key} className="radar34-item" data-testid={`radar-${it.kind}`}>
            <span className="radar34-icon" aria-hidden="true"><Icon name={ICON[it.kind]} size={16} /></span>
            <button className="linklike radar34-who" onClick={() => navigate(personPath(it.personId))}><Avatar name={p?.displayName ?? '?'} size="xs" /><b>{p?.displayName}</b></button>
            <span className="grow small" style={{ minWidth: 0 }}>{radarText(rt, it)}</span>
            <span className="tiny muted num">{it.days === 0 ? t('radar34.today') : t('radar34.in_days', { count: it.days, date: i18n.date(it.date, 'dayMonth') })}</span>
            {it.kind === 'transparency_day'
              ? <button className="btn btn-sm" onClick={() => rt.open(<TransparencySummary personId={it.personId} />)} data-testid="radar-transparency">{t('summary.transparency_open')}</button>
              : done ? <span className="chip ok">{t('radar34.task_done')}</span>
                : <button className="btn btn-sm btn-ghost" onClick={() => void rt.run((v, c) => radarTask(v, it, `${p?.displayName ?? ''}: ${radarText(rt, it)}`, c), 'task.created')} data-testid="radar-task">{t('radar34.task')}</button>}
          </li>
        );
      })}</ul>
      {items.length > 6 && <button className="btn btn-sm btn-ghost" style={{ margin: '0 14px 12px' }} onClick={() => setAll(!all)}>{all ? t('radar34.less') : t('radar34.more', { count: items.length - 6 })}</button>}
    </section>
  );
}
